import { defineStore } from 'pinia';
import { ref } from 'vue';
import { MenuLinkService } from '../services/menuLinkService';
import { GlobalMockupService } from '@/common/services/mockup';
import { MockWardLinksManagementService } from '../services/mockWardLinksManagementService';
import type { MenuLinkPayloadModel, MenuLinkModel } from '../types/menuTypes';
import { LinkTypeEnum } from '../constants/menuConstants';

export const useMenuLinkStore = defineStore('wardMenuLink', () => {
  // State
  const links = ref<MenuLinkModel[]>([]);
  const linksWardLinkType = ref<MenuLinkModel[]>([]);
  const loading = ref(false);
  const error = ref<string | null>(null);

  // Helper to check if mockup is enabled
  const isMockupEnabled = () => GlobalMockupService.isFeatureMockupEnabled('wardMenu');

  // State management functions
  function setLinks(newLinks: MenuLinkModel[]) {
    links.value = newLinks;
  }

  function setError(errorMessage: string | null) {
    error.value = errorMessage;
  }

  function setLoading(isLoading: boolean) {
    loading.value = isLoading;
  }

  function clearError() {
    error.value = null;
  }

  // Utility functions
  function getLinksByModuleId(moduleId: number): MenuLinkModel[] {
    return links.value.filter((link) => link.idModuleWard === moduleId);
  }

  function getLinkById(id: number): MenuLinkModel | undefined {
    return links.value.find((link) => link.id === id);
  }

  function getLinksByWardLinkType(type: string): MenuLinkModel[] {
    return linksWardLinkType.value.filter((link) => link.linkType === LinkTypeEnum.Ward);
  }

  // CRUD Operations - appelant directement les services
  async function fetchLinks() {
    loading.value = true;
    clearError();
    try {
      let fetchedLinks: MenuLinkModel[];
      if (isMockupEnabled()) {
        fetchedLinks = await MockWardLinksManagementService.getLinks();
      } else {
        fetchedLinks = await MenuLinkService.getLinks();
      }
      setLinks(fetchedLinks);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch links');
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function createLink(
    newLink: MenuLinkPayloadModel,
    _position: string = 'after',
    _oldId?: number
  ) {
    loading.value = true;
    clearError();
    try {
      // Set order to end of module links
      const moduleLinks = getLinksByModuleId(newLink.idModuleWard);
      newLink.order = moduleLinks.length;

      let createdLink: MenuLinkModel;
      if (isMockupEnabled()) {
        createdLink = await MockWardLinksManagementService.createLink(newLink);
      } else {
        createdLink = await MenuLinkService.createLink(newLink);
      }

      // Add to array
      const updatedLinks = [...links.value, createdLink];
      setLinks(updatedLinks);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create link');
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function updateLink(
    updatedData: MenuLinkModel,
    _position: string = 'after',
    _oldId?: number
  ) {
    loading.value = true;
    clearError();
    try {
      let updatedLink: MenuLinkModel;
      if (isMockupEnabled()) {
        updatedLink = await MockWardLinksManagementService.updateLink(updatedData);
      } else {
        updatedLink = await MenuLinkService.updateLink(updatedData.id, updatedData);
      }

      // Update in array
      const index = links.value.findIndex((l) => l.id === updatedLink.id);
      if (index !== -1) {
        links.value[index] = updatedLink;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update link');
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function deleteLink(id: number) {
    loading.value = true;
    clearError();
    try {
      if (isMockupEnabled()) {
        await MockWardLinksManagementService.deleteLink(id);
      } else {
        await MenuLinkService.deleteLink(id);
      }

      // Remove from array
      const updatedLinks = links.value.filter((l) => l.id !== id);
      setLinks(updatedLinks);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete link');
      throw err;
    } finally {
      loading.value = false;
    }
  }

  // Update links order in batch - only update links that actually changed
  async function updateLinksOrder(
    updatedLinks: MenuLinkModel[],
    originalLinks: MenuLinkModel[]
  ): Promise<void> {
    loading.value = true;
    clearError();
    try {
      // Create maps for easier comparison
      const originalLinksMap = new Map(originalLinks.map((link) => [link.id, link]));

      // Find links that actually need updating
      const linksToUpdate = updatedLinks.filter((updatedLink) => {
        const originalLink = originalLinksMap.get(updatedLink.id);
        if (!originalLink) {
          return true; // New link, should be updated
        }
        // Check if order changed
        return originalLink.order !== updatedLink.order;
      });

      if (linksToUpdate.length === 0) {
        return;
      }

      // Update each link that needs updating
      const updatePromises = linksToUpdate.map(async (link) => {
        if (isMockupEnabled()) {
          return await MockWardLinksManagementService.updateLink(link);
        } else {
          return await MenuLinkService.updateLink(link.id, link);
        }
      });

      await Promise.all(updatePromises);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update links order');
      throw err;
    } finally {
      loading.value = false;
    }
  }

  return {
    // State
    links,
    loading,
    error,

    // Operations
    fetchLinks,
    createLink,
    updateLink,
    deleteLink,
    updateLinksOrder,

    // State management
    setLinks,
    setError,
    setLoading,
    clearError,

    // Utilities
    getLinksByModuleId,
    getLinkById,
    getLinksByWardLinkType,
  };
});
